import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Interfaz para localidad
 */
export interface Locality {
  id: string;
  code: string;
  name: string;
  provinceName: string;
  provinceId: string;
  status: string;
}

/**
 * Tipo de retorno para paginación server-side
 */
export interface LocalityPageResponse {
  items: Locality[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class LocalityService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  /**
   * Normaliza el status de mayúsculas a minúsculas
   */
  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToLocality(item: any): Locality {
    return {
      id: item.id || '',
      code: item.code || '',
      name: item.name || '',
      provinceName: item.provinceName || '',
      provinceId: item.provinceId || '', 
      status: this.normalizeStatus(item.status),
    };
  }

  /**
   * Obtiene todas las localidades de un país (con paginación)
   */
  getAll(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'desc' | 'asc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<LocalityPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    return this.httpClient
      .get<
        ApiResponse<PageableData>
      >(`${this.apiUrl}/${countryId}/localities`, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((item: any) => this.mapToLocality(item)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  /**
   * Obtiene una localidad por ID
   */
  getById(countryId: string, localityId: string): Observable<Locality> {
    return this.httpClient
      .get<
        ApiResponse<any>
      >(`${this.apiUrl}/${countryId}/localities/${localityId}`)
      .pipe(map((response) => this.mapToLocality(response.data)));
  }

  /**
   * Crea una nueva localidad
   */
  create(
    countryId: string,
    data: { code: string; name: string },
  ): Observable<{ message: string }> {
    return this.httpClient
      .post<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${countryId}/localities`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Localidad creada exitosamente',
        })),
      );
  }

  /**
   * Actualiza una localidad existente
   */
  update(
    countryId: string,
    localityId: string,
    data: { code: string; name: string },
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${countryId}/localities/${localityId}`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Localidad actualizada exitosamente',
        })),
      );
  }

  /**
   * Elimina una localidad
   */
  delete(
    countryId: string,
    localityId: string,
  ): Observable<{ message: string }> {
    return this.httpClient
      .delete<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${countryId}/localities/${localityId}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Localidad eliminada exitosamente',
        })),
      );
  }

  /**
   * Actualiza el status de una localidad
   */
  updateStatus(
    countryId: string,
    localityId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${countryId}/localities/${localityId}/status`, {
        status: status.toUpperCase(),
      })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }
}
